import type { CSSProperties } from 'react';
import { PrototypeIcon } from './PrototypeIcon';
import { ExpensePaymentDistribution, expenseShareBasisPoints } from '@tosho/domain';
import { remunerationMoney } from './RemunerationColumn';
import { StackedPaymentsBar } from './StackedPaymentsBar';
import type { FinancialOverview } from './financial-overview-api';
import { FinancialCalculationStatus } from './FinancialCalculationStatus';

import { expenseVisual } from './expense-visual';
export { expenseVisual } from './expense-visual';

export function ExpenseCategorySymbol({ name }: { readonly name: string }): React.JSX.Element {
  const visual = expenseVisual(name);
  return (
    <span
      className="expense-category-symbol"
      style={{ '--expense-category-color': visual.color } as CSSProperties}
      data-category-color={visual.color}
    >
      <PrototypeIcon name={visual.icon} />
    </span>
  );
}

/** Une comparaison de paiements homogènes, pas un camembert mélangeant HT,
 * TTC et remboursements. Aucun sous-total de charges n'est fabriqué ici.
 */
export function ExpenseDistributionChart({
  categories,
  complete = false,
  showAmounts = false,
  kinds = [],
  billedHtCents = null,
  revenueComplete = false,
}: {
  readonly showAmounts?: boolean;
  readonly kinds?: NonNullable<FinancialOverview['expenseCategoryKinds']>;
  readonly billedHtCents?: string | null;
  readonly revenueComplete?: boolean;
  readonly complete?: boolean;
  readonly categories: readonly {
    sourceConnectionId: string;
    categoryId: string;
    name: string;
    paidCents: string;
    htCents?: string | null;
    personal?: boolean;
  }[];
}): React.JSX.Element | null {
  if (!categories.length) return null;
  const distribution = new ExpensePaymentDistribution(
    categories.map((row) => ({ ...row, paidCents: BigInt(row.paidCents) })),
  );
  const shares = showAmounts ? distribution.positiveShares() : distribution.shares();
  const positiveTotal = categories.reduce(
    (sum, row) => sum + (BigInt(row.paidCents) > 0n ? BigInt(row.paidCents) : 0n),
    0n,
  );
  const maximum = Math.max(10000, ...shares.map((row) => Math.abs(row.basisPoints ?? 0)));
  return (
    <figure className="expense-distribution" aria-label="Répartition des paiements par catégorie">
      <figcaption>
        Répartition de vos paiements
        <small>
          {showAmounts
            ? '% des paiements positifs connus · rémunération incluse si disponible'
            : '% du total net payé connu · hors rémunération'}
        </small>
      </figcaption>
      {!complete ? (
        <small>Répartition partielle · couverture des sources à contrôler en S5</small>
      ) : null}
      {showAmounts ? (
        <StackedPaymentsBar
          parts={categories.map((row, index) => ({
            name: row.name,
            basisPoints: shares[index]?.basisPoints ?? null,
          }))}
        />
      ) : null}
      <ul>
        {categories.map((row, index) => {
          const kind =
            kinds.find(
              (entry) =>
                entry.sourceConnectionId === row.sourceConnectionId &&
                entry.categoryId === row.categoryId,
            )?.kind ?? 'unclassified';
          const ratio =
            !row.personal &&
            revenueComplete &&
            billedHtCents !== null &&
            BigInt(billedHtCents) > 0n &&
            row.htCents != null
              ? expenseShareBasisPoints(BigInt(row.htCents), BigInt(billedHtCents))
              : null;
          const refundBasisPoints =
            showAmounts && BigInt(row.paidCents) < 0n && positiveTotal > 0n
              ? Number((-BigInt(row.paidCents) * 10000n) / positiveTotal)
              : null;
          const trackBasisPoints = shares[index]?.basisPoints ?? refundBasisPoints;
          const cashOnly =
            /^(?:(?:débit de )?tva|remboursement emprunt(?: pro)?|(?:frais de )?débours)$/iu.test(
              row.name.trim(),
            );
          const kindLabel = row.personal
            ? 'Prélèvement personnel'
            : {
                fixed: 'Fixe',
                variable: 'Variable',
                mixed: 'Mixte',
                unclassified: 'À préciser',
              }[kind];
          return (
            <li
              key={JSON.stringify([row.sourceConnectionId, row.categoryId])}
              style={{ '--expense-category-color': expenseVisual(row.name).color } as CSSProperties}
            >
              <span className="expense-distribution__label">
                <ExpenseCategorySymbol name={row.name} />
                <span>{row.name}</span>
                <span className="expense-distribution__numbers">
                  {showAmounts ? (
                    <b>
                      {remunerationMoney(row.paidCents)} <small>payés</small>
                    </b>
                  ) : null}
                  <b>
                    {showAmounts && BigInt(row.paidCents) < 0n ? (
                      'Remboursement'
                    ) : shares[index]?.basisPoints == null ? (
                      <FinancialCalculationStatus
                        calculation="expenseRatio"
                        label="Part indisponible"
                        reason="Le total net payé est nul, négatif ou ambigu ; une répartition en pourcentage ne peut pas être établie sur cette base."
                      />
                    ) : (
                      `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(shares[index].basisPoints / 100)} %`
                    )}
                  </b>
                </span>
              </span>
              {showAmounts && cashOnly ? (
                <small className="expense-distribution__metadata">
                  {kindLabel} · Flux de trésorerie hors ratio de charges HT
                </small>
              ) : null}
              {showAmounts && !cashOnly ? (
                <small className="expense-distribution__metadata">
                  <span>{kindLabel}</span>
                  <span hidden={row.personal || cashOnly}>
                    {row.htCents == null
                      ? 'Base HT absente de la source'
                      : `${remunerationMoney(row.htCents)} HT`}
                  </span>
                  <span hidden={row.personal || cashOnly}>
                    {ratio === null ? (
                      <FinancialCalculationStatus
                        calculation="expenseRatio"
                        label="Ratio au CA non établi"
                      />
                    ) : (
                      `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(ratio / 100)} % du CA HT`
                    )}
                  </span>
                </small>
              ) : null}
              {shares[index]?.basisPoints != null || BigInt(row.paidCents) < 0n ? (
                <span
                  className={`expense-distribution__track${BigInt(row.paidCents) < 0n ? ' is-refund' : ''}`}
                  aria-hidden="true"
                >
                  <i
                    style={{
                      width: `${showAmounts ? Math.min(100, Math.abs(trackBasisPoints ?? 0) / 100) : (Math.abs(shares[index]?.basisPoints ?? 0) / maximum) * 100}%`,
                    }}
                  />
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      <small>
        {showAmounts
          ? 'Parts des montants positifs affichés, pas du CA. Les remboursements sont présentés à part. La rémunération est un prélèvement personnel, distinct des charges.'
          : 'Parts du total payé, pas du CA. Les remboursements restent négatifs ; les arrondis peuvent écarter la somme de 100 %.'}
      </small>
    </figure>
  );
}
